import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useMemo,
} from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';

import { COMPLETE_ORDER, OPEN_ORDER } from '../mutations';
import { useNavigate } from 'react-router-dom';
import { ORDER_QUERY } from '../queries';
import { isStripePaymentRequired } from '../utils/featureFlags';

const initialState = {
  orderDetails: {},
  customer: {},
  paymentMethodType: 'new',
  paymentMethod: null,
  products: [],
  promoCodeApplied: null,
  totalPrice: null,
  totalDiscount: null,
  loadingCustomer: false,
  errorCustomer: null,
  loadingOpenOrder: false,
  errorOpenOrder: null,
  loadingApplyPromoCode: false,
  errorApplyPromoCode: null,
  loadingAddOnToOrder: false,
  errorAddOnToOrder: null,
  loadingRemoveFromOrder: false,
  errorRemoveFromOrder: null,
  loadingGetOrder: false,
  errorGetOrder: null,
};

export const SET_ORDER_DETAILS = 'set-order-details';
export const SET_CUSTOMER = 'set-customer';
export const SET_LOADING_CUSTOMER = 'set-loading-customer';
export const SET_ERROR_CUSTOMER = 'set-error-customer';
export const SET_PAYMENT_METHOD = 'set-payment-method';
export const SET_PRODUCTS = 'set-products';
export const SET_ATTR = 'set-attr';
export const SET_ORDER_STATUS = 'set-order-status';
export const UPDATE_PRODUCTS = 'update-products';
export const SET_PROMO_CODE_APPLIED = 'set-promo-code-applied';

function reducer(state, action) {
  const { type, payload } = action;

  switch (type) {
    case SET_ORDER_DETAILS: {
      return {
        ...state,
        orderDetails: {
          ...state.orderDetails,
          ...payload,
        },
      };
    }
    case SET_CUSTOMER: {
      return {
        ...state,
        customer: payload,
      };
    }
    case SET_LOADING_CUSTOMER: {
      return { ...state, loadingCustomer: payload };
    }
    case SET_ERROR_CUSTOMER: {
      return { ...state, errorCustomer: payload };
    }
    case SET_PAYMENT_METHOD: {
      const { paymentMethod, paymentMethodType } = payload;
      return {
        ...state,
        paymentMethod: paymentMethod ? paymentMethod : state.paymentMethod,
        paymentMethodType: paymentMethodType
          ? paymentMethodType
          : state.paymentMethodType,
      };
    }
    case SET_PRODUCTS: {
      return {
        ...state,
        products: payload,
      };
    }
    case SET_ATTR: {
      const { name, value } = payload;
      return {
        ...state,
        [name]: value,
      };
    }
    case SET_ORDER_STATUS: {
      const { orderStatus, orderId } = payload;
      return {
        ...state,
        orderDetails: {
          ...state.orderDetails,
          orderId,
        },
        orderStatus,
      };
    }
    case UPDATE_PRODUCTS: {
      const { totalPrice, lineItems } = payload;
      return {
        ...state,
        products: lineItems,
        totalPrice,
      };
    }
    case SET_PROMO_CODE_APPLIED: {
      return {
        ...state,
        promoCodeApplied: payload,
      };
    }
    default: {
      return { ...state };
    }
  }
}

const OrderContext = createContext();

export function OrderProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const navigate = useNavigate();

  useEffect(() => {
    if (!isStripePaymentRequired()) {
      dispatch({
        type: SET_PAYMENT_METHOD,
        payload: {
          paymentMethod: { id: 'bypass' },
          paymentMethodType: 'bypass',
        },
      });
    }
  }, []);

  const [
    openOrder,
    { loading: loadingOpenOrder, error: errorOpenOrder, data: dataOpenOrder },
  ] = useMutation(OPEN_ORDER);

  useEffect(() => {
    dispatch({
      type: SET_ATTR,
      payload: {
        name: 'loadingOpenOrder',
        value: loadingOpenOrder,
      },
    });
  }, [loadingOpenOrder]);

  useEffect(() => {
    dispatch({
      type: SET_ATTR,
      payload: {
        name: 'errorOpenOrder',
        value: errorOpenOrder,
      },
    });
  }, [errorOpenOrder]);

  useEffect(() => {
    const order = dataOpenOrder?.openOrder;
    if (order) {
      dispatch({
        type: SET_ORDER_STATUS,
        payload: {
          orderStatus: order.status,
          orderId: order.id,
        },
      });
      dispatch({
        type: UPDATE_PRODUCTS,
        payload: {
          lineItems: order.invoice.lineItems,
          totalPrice: order.invoice.total,
        },
      });
    }
  }, [dataOpenOrder]);

  const [
    completeOrder,
    {
      loading: loadingCompleteOrder,
      error: errorCompleteOrder,
      data: dataCompleteOrder,
    },
  ] = useMutation(COMPLETE_ORDER);

  useEffect(() => {
    dispatch({
      type: SET_ATTR,
      payload: {
        name: 'loadingCompleteOrder',
        value: loadingCompleteOrder,
      },
    });
  }, [loadingCompleteOrder]);

  useEffect(() => {
    dispatch({
      type: SET_ATTR,
      payload: {
        name: 'errorCompleteOrder',
        value: errorCompleteOrder,
      },
    });
  }, [errorCompleteOrder]);

  useEffect(() => {
    const order = dataCompleteOrder?.completeOrder;
    if (order) {
      // profileOrderId is set when the order chained an audience-profile
      // order ("Complete and Profile"); the summary page uses it to show
      // the report-is-generating banner.
      navigate(order.id, {
        state: order.profileOrderId
          ? { profileOrderId: order.profileOrderId }
          : undefined,
      });
    }
  }, [dataCompleteOrder, navigate]);

  const [
    getOrder,
    {
      loading: loadingGetOrder,
      error: errorGetOrder,
      data: dataGetOrder,
      called: calledGetOrder,
    },
  ] = useLazyQuery(ORDER_QUERY);

  useEffect(() => {
    dispatch({
      type: SET_ATTR,
      payload: {
        name: 'loadingGetOrder',
        value: loadingGetOrder,
      },
    });
  }, [loadingGetOrder]);

  useEffect(() => {
    dispatch({
      type: SET_ATTR,
      payload: {
        name: 'errorGetOrder',
        value: errorGetOrder,
      },
    });
  }, [errorGetOrder]);

  useEffect(() => {
    const order = dataGetOrder?.order;
    if (order && calledGetOrder) {
      dispatch({
        type: SET_ORDER_STATUS,
        payload: {
          orderStatus: order.status,
          orderId: order.id,
        },
      });
      if (order.invoice) {
        dispatch({
          type: UPDATE_PRODUCTS,
          payload: {
            lineItems: order.invoice.lineItems,
            totalPrice: order.invoice.total,
          },
        });
      }

      if (order.promoCode) {
        dispatch({
          type: SET_PROMO_CODE_APPLIED,
          payload: {
            ...order.invoice.discount,
            code: order.promoCode,
          },
        });
      }
    }
  }, [calledGetOrder, dataGetOrder]);

  const loading = useMemo(
    () => state.loadingCustomer || state.loadingOpenOrder,
    [state.loadingCustomer, state.loadingOpenOrder]
  );
  const error = useMemo(
    () =>
      state.errorCustomer || state.errorOpenOrder || state.errorCompleteOrder,
    [state.errorCustomer, state.errorOpenOrder, state.errorCompleteOrder]
  );

  const value = {
    state,
    dispatch,
    loading,
    error,
    openOrder,
    completeOrder,
    loadOrder: getOrder,
  };

  return (
    <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
  );
}

export function useOrder() {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within a OrderProvider');
  }

  return context;
}
